import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { ExerciseSession } from "@/components/exercise-session";
import { ListeningControls } from "@/components/listening-controls";
import { PlaySoundsButton } from "@/components/ui/play-sounds-button";
import { ProgressBar } from "@/components/ui/progress-bar";
import { useT } from "@/glossary";
import {
  stopAllSounds,
  usePlayBundledSoundsAction,
} from "@/lib/audio/use-play-sound";
import { usePlaySuccessSoundIfEnabled } from "@/lib/audio/use-success-sound";
import { PLAYBACK_SEQUENCE_GAP_MS, PLAYBACK_WARMUP_MS } from "@/lib/constants";
import type { OMO } from "@/lib/data/words";
import { omoStore } from "@/lib/stores/omo";
import { useEffectOnce } from "@/lib/use-effect-once";
import { buildDisplayItems, WordOptions } from "./-word-options";

export const Route = createFileRoute("/omo/exercise")({
  component: OmoExercise,
});

function OmoExercise() {
  return (
    <ExerciseSession id="omo">
      <ActiveExercise />
    </ExerciseSession>
  );
}

const ActiveExercise = () => {
  const navigate = useNavigate();
  const [hasStarted, setHasStarted] = useState(false);
  const wordIndex = omoStore.useCurrentWordIndex();
  const view = omoStore.useCurrent();

  useEffect(() => () => stopAllSounds(), []);

  if (view.kind !== "SHOW_CURRENT_WORD") return null;

  const { word, progress } = view.data;
  return (
    <ExerciseWord
      key={wordIndex}
      hasStarted={hasStarted}
      word={word}
      progress={progress}
      onStart={() => setHasStarted(true)}
      onNext={() => {
        const { isLast } = omoStore.confirmResult();
        if (isLast) {
          navigate({ to: "/omo/results" });
        }
      }}
    />
  );
};

const ExerciseWord = ({
  word,
  progress,
  hasStarted,
  onStart,
  onNext,
}: {
  word: (typeof OMO)[number];
  progress: { current: number; total: number };
  hasStarted: boolean;
  onStart: () => void;
  onNext: () => void;
}) => {
  const t = useT();
  const [selected, setSelected] = useState<number | null>(null);
  const [autoplayDone, setAutoplayDone] = useState(false);
  const playSuccessSoundIfEnabled = usePlaySuccessSoundIfEnabled();
  const isLastWord = progress.current + 1 === progress.total;

  const displayItems = useMemo(() => buildDisplayItems(word), [word]);
  const { oddOnePosition } = word;

  const baseFileNames = useMemo(
    () => displayItems.map((d) => d.audio.baseFileName),
    [displayItems],
  );
  const playbackDurationMs = useMemo(
    () =>
      PLAYBACK_WARMUP_MS +
      displayItems.reduce((total, item) => total + item.audio.durationMs, 0) +
      PLAYBACK_SEQUENCE_GAP_MS * Math.max(0, displayItems.length - 1),
    [displayItems],
  );
  const playAll = usePlayBundledSoundsAction(baseFileNames);

  useEffectOnce({
    effect: () => {
      if (hasStarted) playAll(() => setAutoplayDone(true));
    },
    onError: () => {
      if (hasStarted) setAutoplayDone(true);
    },
  });

  const hasAnswered = selected !== null;
  const isCorrect = hasAnswered && selected === oddOnePosition;

  const handleSelect = (index: number) => {
    setSelected(index);
    const correct = index === oddOnePosition;

    omoStore.addAttempt({
      selected: index,
      correct,
      timestamp: Date.now(),
    });

    if (correct) playSuccessSoundIfEnabled();
  };

  return (
    <div className="flex flex-1 flex-col px-4">
      <div className="flex flex-1 flex-col items-center gap-6">
        <ProgressBar
          current={progress.current + 1}
          total={progress.total}
          label={t.common.wordProgress(progress.current + 1, progress.total)}
        />

        <div className="space-y-2 text-center">
          <p className="font-medium text-base text-foreground">
            {t.omo.instruction}
          </p>
          <p className="text-muted-foreground text-sm">
            {t.omo.clickNumberToAnswer}
          </p>
        </div>

        <WordOptions
          displayItems={displayItems}
          selected={selected}
          oddOnePosition={oddOnePosition}
          syllableCount={word.syllableCount}
          highlightAll={!hasAnswered && autoplayDone}
          onSelect={autoplayDone ? handleSelect : undefined}
        />

        {hasAnswered && (
          <p aria-live="polite" className="font-semibold text-lg">
            {isCorrect ? t.common.goodJob : t.common.notQuite}
          </p>
        )}
      </div>

      <ListeningControls
        hasStarted={hasStarted}
        onStart={() => {
          onStart();
          try {
            playAll(() => setAutoplayDone(true));
          } catch {
            setAutoplayDone(true);
          }
        }}
        hasAnswered={hasAnswered}
        replayButton={
          <PlaySoundsButton
            baseFileNames={baseFileNames}
            playbackDurationMs={playbackDurationMs}
            label={t.omo.replayWords}
            playingLabel={t.omo.playing}
            nudge={hasAnswered && !isCorrect}
          />
        }
        onNext={onNext}
        nextLabel={isLastWord ? t.common.next : t.omo.nextWord}
      />
    </div>
  );
};
